import React from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  Code,
  Group,
  Paper,
  Stack,
  Text,
  ThemeIcon,
  Title,
} from '@mantine/core';
import {
  IconAlertTriangle,
  IconArrowLeft,
  IconBug,
  IconHome,
  IconLock,
  IconMapOff,
  IconRefresh,
  IconSettings,
  IconShieldX,
  IconWifiOff,
} from '@tabler/icons-react';
import AppSettings from '../../components/settings/AppSettings';
import type { AppErrorAction, AppErrorDetails, AppErrorKind } from '../../errors/appErrorTypes';
import { DEFAULT_APP_ERROR } from '../../errors/appErrorTypes';
import { consumePersistedAppError } from '../../errors/appErrorStorage';
import { createConfigError } from '../../errors/normalizeAppError';
import { redirectToPortal, resolvePortalRedirectUrl } from '../../utils/logout';
import { APP_ROUTES } from '../../navigation/appRoutes';

const KIND_META: Record<
  AppErrorKind,
  { color: string; icon: typeof IconAlertTriangle }
> = {
  config: { color: 'orange', icon: IconSettings },
  auth: { color: 'red', icon: IconLock },
  session: { color: 'red', icon: IconShieldX },
  network: { color: 'grape', icon: IconWifiOff },
  not_found: { color: 'gray', icon: IconMapOff },
  runtime: { color: 'red', icon: IconBug },
  unknown: { color: 'yellow', icon: IconAlertTriangle },
};

type LocationState = {
  error?: AppErrorDetails;
};

export interface AppErrorPageProps {
  error?: AppErrorDetails;
  /** Show dev settings entry (bootstrap / config errors). */
  showDevSettings?: boolean;
}

function resolveError(
  propError?: AppErrorDetails,
  locationState?: LocationState | null,
): AppErrorDetails {
  if (propError?.message) {
    return propError;
  }
  if (locationState?.error?.message) {
    return locationState.error;
  }
  const persisted = consumePersistedAppError();
  if (persisted) {
    return persisted;
  }
  return DEFAULT_APP_ERROR;
}

function defaultActions(
  error: AppErrorDetails,
  opts: { showDevSettings: boolean; portalUrl: string },
): AppErrorAction[] {
  if (error.actions?.length) {
    return error.actions;
  }

  const actions: AppErrorAction[] = [];
  const isSessionOrAuth = error.kind === 'session' || error.kind === 'auth';

  if (isSessionOrAuth) {
    actions.push({
      id: 'portal',
      label: opts.portalUrl ? 'Open Portal2' : 'Return to Portal',
      primary: true,
    });
    if (opts.showDevSettings) {
      actions.push({ id: 'settings', label: 'Configure settings' });
    }
    return actions;
  }

  if (opts.showDevSettings && error.kind === 'config') {
    actions.push({ id: 'settings', label: 'Configure settings', primary: true });
  }

  if (error.kind !== 'not_found') {
    actions.push({ id: 'retry', label: 'Reload page', primary: actions.length === 0 });
  }

  actions.push({ id: 'home', label: 'Go to dashboard' });

  return actions;
}

const AppErrorPage: React.FC<AppErrorPageProps> = ({
  error: errorProp,
  showDevSettings = false,
}) => {
  const location = useLocation();
  const navigate = useNavigate();
  const [settingsOpened, setSettingsOpened] = React.useState(false);

  const error = React.useMemo(
    () => resolveError(errorProp, location.state as LocationState | null),
    [errorProp, location.state],
  );

  const meta = KIND_META[error.kind] ?? KIND_META.unknown;
  const KindIcon = meta.icon;
  const portalUrl = resolvePortalRedirectUrl();
  const actions = defaultActions(error, { showDevSettings, portalUrl });

  const runAction = (action: AppErrorAction) => {
    switch (action.id) {
      case 'retry':
        window.location.reload();
        break;
      case 'home':
        navigate(APP_ROUTES.dashboard.overview, { replace: true });
        break;
      case 'portal':
        if (portalUrl) {
          redirectToPortal(portalUrl);
        }
        break;
      case 'settings':
        setSettingsOpened(true);
        break;
      default:
        break;
    }
  };

  return (
    <Box
      style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        minHeight: '100vh',
        padding: 24,
        backgroundColor: 'var(--app-shell-bg, var(--mantine-color-gray-1))',
      }}
    >
      <Paper shadow="md" p="xl" radius="md" style={{ maxWidth: 560, width: '100%' }}>
        <Stack gap="lg">
          <Group gap="md" wrap="nowrap" align="flex-start">
            <ThemeIcon size={52} radius="md" variant="light" color={meta.color}>
              <KindIcon size={28} stroke={1.5} />
            </ThemeIcon>
            <Box style={{ flex: 1, minWidth: 0 }}>
              <Title order={2} size="h3">
                {error.title}
              </Title>
              <Text size="sm" c="dimmed" mt={4}>
                {error.message}
              </Text>
            </Box>
          </Group>

          {error.statusCode != null ? (
            <Alert variant="light" color="gray" title="Status">
              HTTP {error.statusCode}
            </Alert>
          ) : null}

          {error.detail ? (
            <Box>
              <Text size="xs" c="dimmed" mb={4}>
                Details
              </Text>
              <Code block style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                {error.detail}
              </Code>
            </Box>
          ) : null}

          {error.kind === 'config' ? (
            <Text size="sm" c="dimmed">
              Open the scheduler from Portal2 so{' '}
              <Code>{'jwtAuth_{env}'}</Code> and <Code>{'projectsInfo_{env}'}</Code> cookies are
              set, or use Configure settings for local development.
            </Text>
          ) : null}

          {(error.kind === 'session' || error.kind === 'auth') && !portalUrl ? (
            <Text size="sm" c="dimmed">
              No Portal2 link was found. Set <Code>VITE_MAIN_PORTAL_BASEURL</Code> or open the
              scheduler from Portal2 so a return URL is available.
            </Text>
          ) : null}

          <Group gap="sm" grow preventGrowOverflow={false}>
            {actions.map((action) => {
              const isPrimary = !!action.primary;
              if (action.id === 'home') {
                return (
                  <Button
                    key={action.id}
                    component={Link}
                    to={APP_ROUTES.dashboard.overview}
                    variant={isPrimary ? 'filled' : 'default'}
                    leftSection={<IconHome size={18} />}
                  >
                    {action.label}
                  </Button>
                );
              }
              return (
                <Button
                  key={action.id}
                  variant={isPrimary ? 'filled' : 'default'}
                  color={action.id === 'portal' ? 'brand' : undefined}
                  disabled={action.id === 'portal' && !portalUrl}
                  leftSection={
                    action.id === 'retry' ? (
                      <IconRefresh size={18} />
                    ) : action.id === 'settings' ? (
                      <IconSettings size={18} />
                    ) : action.id === 'portal' ? (
                      <IconArrowLeft size={18} />
                    ) : undefined
                  }
                  onClick={() => runAction(action)}
                >
                  {action.label}
                </Button>
              );
            })}
          </Group>
        </Stack>
      </Paper>

      {showDevSettings ? (
        <AppSettings opened={settingsOpened} onClose={() => setSettingsOpened(false)} />
      ) : null}
    </Box>
  );
};

export default AppErrorPage;

/** Pre-built config error for missing portal session (bootstrap). */
export function ConfigRequiredErrorPage({ message }: { message: string }) {
  return (
    <AppErrorPage
      error={createConfigError(
        message,
        'Set VITE_PROJECT_ID and VITE_AUTH_TOKEN in .env for local development.',
      )}
      showDevSettings
    />
  );
}
